import React, { useState } from "react";
import { ChevronRight, ChevronDown, Folder, FolderOpen } from "lucide-react";

/**
 * Hierarchical category tree (max-3 deep) rendered from the /api/categories response.
 * - tree: backend tree array of { name, path, self_count, total, children[] }
 * - active: currently selected path string ("" = All)
 * - onSelect: (path: string) => void
 */
export default function CategoryTree({ tree = [], active = "", onSelect, totalAll = 0 }) {
  return (
    <div className="brutal-card p-4" data-testid="category-tree">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] mb-3">Folders</div>
      <button
        onClick={() => onSelect("")}
        className={`w-full text-left flex items-center justify-between px-2 py-2 border-2 ${active === "" ? "border-black bg-[#111] text-white" : "border-transparent hover:bg-[#F4F2EC]"}`}
        data-testid="folder-all"
      >
        <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
          <Folder size={14}/> All Items
        </span>
        <span className="font-mono text-[10px]">{totalAll}</span>
      </button>
      <div className="mt-2 space-y-1">
        {tree.map((node) => (
          <TreeNode key={node.path} node={node} depth={0} active={active} onSelect={onSelect}/>
        ))}
      </div>
    </div>
  );
}

function TreeNode({ node, depth, active, onSelect }) {
  const [open, setOpen] = useState(active.startsWith(node.path));
  const hasChildren = (node.children || []).length > 0;
  const selected = active === node.path;

  return (
    <div>
      <div className={`group flex items-center gap-1`} style={{ paddingLeft: depth * 14 }}>
        <button
          type="button"
          onClick={() => hasChildren && setOpen(!open)}
          className="w-5 h-5 flex items-center justify-center text-[#4A4A4A]"
          aria-label={hasChildren ? (open ? "Collapse" : "Expand") : "Leaf"}
          data-testid={`tree-toggle-${node.path}`}
        >
          {hasChildren ? (open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>) : <span className="w-2 h-2 bg-[#7A7770] inline-block rounded-full"/>}
        </button>
        <button
          onClick={() => onSelect(node.path)}
          className={`flex-1 text-left flex items-center justify-between px-2 py-1.5 border-2 transition-colors ${selected ? "border-black bg-[#C2410C] text-white" : "border-transparent hover:bg-[#F4F2EC]"}`}
          data-testid={`folder-${node.path}`}
        >
          <span className="inline-flex items-center gap-2 font-mono text-xs">
            {open && hasChildren ? <FolderOpen size={13}/> : <Folder size={13}/>}
            <span className="truncate">{node.name}</span>
          </span>
          <span className="font-mono text-[10px] opacity-80">{node.total}</span>
        </button>
      </div>
      {hasChildren && open && (
        <div className="mt-1 space-y-1">
          {node.children.map((c) =>
            React.createElement(TreeNode, { key: c.path, node: c, depth: depth + 1, active, onSelect })
          )}
        </div>
      )}
    </div>
  );
}
